import { supabase } from './supabase';

// Front-end wrapper around the `send-email` Edge Function.

export interface Recipient {
  email: string;
  name?: string | null;
}

export interface EmailAttachment {
  filename: string;
  // Inline content (base64, no data: prefix) …
  contentBase64?: string;
  contentType?: string;
  // …or a file the function pulls from private storage server-side.
  bucket?: string;
  path?: string;
}

export interface SendEmailArgs {
  recipients: Recipient[];
  subject: string;
  message: string;
  attachments?: EmailAttachment[];
  signature?: string;
}

export interface SendEmailResult {
  sent: number;
  total: number;
  failures?: string[];
  message?: string;
}

export async function sendEmail(args: SendEmailArgs): Promise<SendEmailResult> {
  const { data, error } = await supabase.functions.invoke('send-email', { body: args });
  if (error) {
    // The function returns a JSON error body; surface it if present.
    let detail = error.message;
    try {
      const ctx = (error as { context?: Response }).context;
      if (ctx && typeof ctx.json === 'function') {
        const body = await ctx.json();
        if (body?.error) detail = body.error;
      }
    } catch {
      // fall back to error.message
    }
    throw new Error(detail);
  }
  return data as SendEmailResult;
}

// Read a browser File into an attachment (base64). Caps at ~8 MB so we
// don't build an enormous request body.
export const MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024;

export function fileToAttachment(file: File): Promise<EmailAttachment> {
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return Promise.reject(new Error('Attachment is too large (max 8 MB).'));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.onload = () => {
      const result = String(reader.result);
      const base64 = result.includes(',') ? result.slice(result.indexOf(',') + 1) : result;
      resolve({
        filename: file.name,
        contentBase64: base64,
        contentType: file.type || 'application/octet-stream',
      });
    };
    reader.readAsDataURL(file);
  });
}
